import type { CustomerRequestSeed } from '../../../../customerRequests/CustomerRequestComposer';
import React from "react";
import { useTranslation } from "react-i18next";
import { primaryText, secondaryText, greyTextColor, textBlue, borderPrimary } from "../../../../../styling/stylingHelper";
import { eur } from "../../../../orderhistory/utils";
import { getPortionsUnitDisplay } from "../../../../../utils/ingredientGrouping";
import IngredientsSection from "./IngredientsSection";

const DeleteIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.5 5H4.16667H17.5" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.66699 5V3.33333C6.66699 2.89131 6.84259 2.46738 7.15515 2.15482C7.46771 1.84226 7.89164 1.66667 8.33366 1.66667H11.667C12.109 1.66667 12.5329 1.84226 12.8455 2.15482C13.158 2.46738 13.3337 2.89131 13.3337 3.33333V5M15.8337 5V16.6667C15.8337 17.1087 15.6581 17.5326 15.3455 17.8452C15.033 18.1577 14.609 18.3333 14.167 18.3333H5.83366C5.39164 18.3333 4.96771 18.1577 4.65515 17.8452C4.34259 17.5326 4.16699 17.1087 4.16699 16.6667V5H15.8337Z" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.33301 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11.667 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

interface NestedRow {
    id: string;
    nestedSubrecipeId: string;
    name: string;
    isOrphan: boolean;
    portionsNeeded: number;
    portionsUnit: string;
    cost: number;
}

interface SubrecipeDraftIngredientsProps {
    requestContext?: CustomerRequestSeed;
    requestDisabled?: boolean;
    // Normal ingredient rows, already enriched with `currentPrice`.
    normalIngredients: any[];
    nestedRows: NestedRow[];
    inlineQuantities: Record<string, string>;
    inlineWaste: Record<string, string>;
    confirmingDeleteId: string | null;
    defaultWastePercentage: number;
    onQuantityChange: (id: string, value: string) => void;
    onSaveEditQuantity: (id: string, qty: string) => void;
    onWasteChange: (id: string, value: string) => void;
    onSaveWaste: (id: string, value: string) => void;
    onDeleteRow: (ingredient: { id: string }) => void;
    onConfirmDeleteRow: (ingredient: { id: string }) => void;
    onNestedQuantityChange: (id: string, value: string) => void;
    // Click a nested-subrecipe row to drill into its read-only detail view.
    onOpenNested: (nestedSubrecipeId: string) => void;
    // Open the Alternatives editor for a normal (order-history) ingredient row.
    onOpenAlternatives: (ingredient: { id: string }) => void;
    // Open the link picker for a CUSTOM row (`is_custom`), re-pointing the
    // placeholder at real purchase data. Omitted → no link affordance.
    onLinkCustomIngredient?: (ingredient: { id: string }) => void;
    // On mount, focus the normal row whose resolver key matches (setup flow lands
    // the user on the just-seeded ingredient). Handled by IngredientsSection.
    focusProductKey?: string;
}

/**
 * The draft ingredient list for the subrecipe side modal. Normal ingredient rows
 * reuse the shared `IngredientsSection` card (inline quantity, waste %, delete,
 * live row cost); nested-subrecipe references render as their own cards showing
 * portions-needed instead of a quantity.
 */
const SubrecipeDraftIngredients: React.FC<SubrecipeDraftIngredientsProps> = ({
    requestContext, requestDisabled, normalIngredients,
    nestedRows,
    inlineQuantities,
    inlineWaste,
    confirmingDeleteId,
    defaultWastePercentage,
    onQuantityChange,
    onSaveEditQuantity,
    onWasteChange,
    onSaveWaste,
    onDeleteRow,
    onConfirmDeleteRow,
    onNestedQuantityChange,
    onOpenNested,
    onOpenAlternatives,
    onLinkCustomIngredient,
    focusProductKey,
}) => {
    const { t } = useTranslation();

    return (
        <>
            {nestedRows.length > 0 && (
                <div className={`rounded-xl border ${borderPrimary} bg-background dark:bg-background-dark p-4 mt-5`}>
                    <div className="flex items-center gap-2 mb-4">
                        <span className={`text-[16px] leading-5 font-semibold ${primaryText}`}>
                            {t("costPrice.subrecipes.sectionHeader")}
                        </span>
                    </div>

                    <div className="space-y-3">
                        {nestedRows.map(row => {
                            const qtyStr = inlineQuantities[row.id] ?? row.portionsNeeded.toString();
                            return (
                                <div key={row.id} className={`rounded-lg border py-[11px] pl-3 pr-4 flex flex-col bg-backgroundSecondary dark:bg-backgroundSecondary-dark ${borderPrimary}`}>
                                    {/* Row 1: name + tag + delete */}
                                    <div className="flex justify-between items-start mb-2">
                                        <button
                                            type="button"
                                            onClick={() => !row.isOrphan && onOpenNested(row.nestedSubrecipeId)}
                                            className="flex items-center gap-2 min-w-0 flex-1 flex-wrap text-left"
                                        >
                                            <p className={`text-[16px] leading-6 font-semibold tracking-[-0.16px] ${primaryText} min-w-0 truncate`}>
                                                {row.name}
                                            </p>
                                            <span className={`text-[12px] font-normal ${greyTextColor}`}>
                                                {t("costPrice.subrecipes.subrecipeTag")}
                                            </span>
                                        </button>
                                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                            {confirmingDeleteId === row.id ? (
                                                <button type="button" onClick={() => onConfirmDeleteRow({ id: row.id })} className="h-8 px-3 text-[14px] font-semibold bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white dark:text-white rounded-lg transition-colors">
                                                    {t("costPrice.subrecipes.removeAction")}
                                                </button>
                                            ) : (
                                                <button type="button" onClick={() => onDeleteRow({ id: row.id })} className="p-1 hover:opacity-70 transition-opacity">
                                                    <DeleteIcon />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 2: portions needed */}
                                    <div className="flex items-center justify-between gap-3 mt-2">
                                        <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                            {t("costPrice.subrecipes.portionsNeededLabel")}
                                        </p>
                                        <div className="flex items-center gap-1">
                                            <input
                                                type="number" inputMode="decimal" min={0} step="any"
                                                value={qtyStr}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) onNestedQuantityChange(row.id, value);
                                                }}
                                                onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                                                className="w-16 text-right bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded px-2 py-0.5 text-[14px] leading-5 font-medium text-[#0C111D] dark:text-textPrimary-dark outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                            />
                                            <span className={`text-[14px] leading-5 font-medium ${secondaryText}`}>
                                                {getPortionsUnitDisplay(row.portionsUnit)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Row 3: cost */}
                                    <div className="flex justify-between items-center gap-2 mt-2">
                                        <p className={`text-[14px] leading-[21px] font-normal tracking-[-0.14px] ${greyTextColor}`}>
                                            {t("costPrice.subrecipes.costLabel")}
                                        </p>
                                        <p className={`text-[14px] leading-5 font-medium ${textBlue}`}>{eur(row.cost)}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <IngredientsSection
                requestContext={requestContext}
                requestDisabled={requestDisabled}
                ingredients={normalIngredients}
                inlineQuantities={inlineQuantities}
                confirmingDeleteId={confirmingDeleteId}
                onQuantityChange={onQuantityChange}
                onSaveEdit={(id, qty) => onSaveEditQuantity(id, qty)}
                onDelete={onDeleteRow}
                onConfirmDelete={onConfirmDeleteRow}
                defaultWastePercentage={defaultWastePercentage}
                inlineWaste={inlineWaste}
                onWasteChange={onWasteChange}
                onSaveWaste={onSaveWaste}
                onOpenAlternatives={onOpenAlternatives}
                onLinkCustomIngredient={onLinkCustomIngredient}
                focusProductKey={focusProductKey}
            />
        </>
    );
};

export default SubrecipeDraftIngredients;
